import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import MovieCard from "@/components/feature/MovieCard";
import PlotSection from "@/pages/detail/components/PlotSection";
import CharacterSection from "@/pages/detail/components/CharacterSection";
import { useFavorites } from "@/hooks/useFavorites";
import { formatViews, getMovieById, getRelatedMovies } from "@/utils/movieHelpers";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;
  const { toggleFavorite, isFavorite } = useFavorites();
  const [tip, setTip] = useState("");
  const tipTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (tipTimer.current) window.clearTimeout(tipTimer.current);
    };
  }, []);

  const showTip = (message: string) => {
    setTip(message);
    if (tipTimer.current) window.clearTimeout(tipTimer.current);
    tipTimer.current = window.setTimeout(() => setTip(""), 2500);
  };

  if (!movie) {
    return (
      <>
        <Seo
          title="未找到该影视作品 - 肮脏的交易"
          description="抱歉，暂时没有找到该影视作品的相关信息，请返回首页浏览更多内容。"
          keywords="影视作品,未找到,肮脏的交易"
        />
        <section className="mx-auto flex w-full max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6 md:py-32">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-4xl" />
          </span>
          <h1 className="mt-6 text-xl font-bold text-foreground-950 md:text-2xl">
            抱歉，没有找到这部影视作品
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-500">
            该作品可能已下架或链接有误。你可以返回首页，或使用搜索寻找感兴趣的内容。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
            <Link
              to="/search"
              className="rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer whitespace-nowrap"
            >
              去搜索
            </Link>
          </div>
        </section>
      </>
    );
  }

  const favorited = isFavorite(movie.id);
  const related = getRelatedMovies(movie, 4);
  const categoryLabel = movie.type === "movie" ? "电影" : "电视剧";

  const metaList = [
    { label: "导演", value: movie.director, icon: "ri-movie-2-line" },
    { label: "主演", value: movie.cast.join("、"), icon: "ri-group-line" },
    { label: "上映日期", value: movie.releaseDate, icon: "ri-calendar-line" },
    { label: "类型", value: movie.genres.join(" / "), icon: "ri-price-tag-3-line" },
    { label: "片长", value: movie.duration, icon: "ri-time-line" },
    { label: "地区", value: movie.region, icon: "ri-earth-line" },
  ];

  const handleShare = async () => {
    const shareUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(shareUrl);
      showTip("分享链接已复制到剪贴板");
    } catch {
      showTip(`分享链接：${shareUrl}`);
    }
  };

  return (
    <>
      <Seo
        title={`${movie.title} - 剧情解析与资讯 - 肮脏的交易`}
        description={movie.summary}
        keywords={`${movie.title},${movie.genres.join(",")},剧情解析,${movie.director},${categoryLabel}资讯`}
      />

      <section className="relative h-[300px] w-full overflow-hidden bg-primary-950 md:h-[400px]">
        <img
          src={movie.backdrop}
          alt={movie.title}
          title={`${movie.title} - 剧情解析与资讯`}
          className="absolute inset-0 h-full w-full object-cover object-center opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/40" />
        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1280px] flex-col justify-between px-4 py-5 md:px-6 md:py-7">
          <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-sm text-background-200">
            <Link to="/" className="transition-colors hover:text-accent-400 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link
              to={`/category/${movie.type}`}
              className="transition-colors hover:text-accent-400 cursor-pointer"
            >
              {categoryLabel}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">{movie.title}</span>
          </nav>
          <div className="max-w-3xl">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-background-50">
                {categoryLabel}
              </span>
              <span className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs text-background-100 ring-1 ring-background-50/30">
                {movie.year} · {movie.region}
              </span>
              <span className="flex items-center gap-1 rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-50 ring-1 ring-background-50/30">
                <i className="ri-star-fill text-accent-400" />
                {movie.rating.toFixed(1)} 分
              </span>
            </div>
            <h1 className="text-2xl font-bold leading-tight text-background-50 md:text-4xl">
              {movie.title}
            </h1>
            <div className="mt-3 flex flex-wrap gap-2">
              {movie.genres.map((g) => (
                <span
                  key={g}
                  className="rounded-md border border-background-50/30 bg-background-50/10 px-2.5 py-1 text-xs text-background-100 whitespace-nowrap"
                >
                  {g}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr] lg:gap-8">
          <aside className="flex flex-col gap-5">
            <div className="w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src={movie.poster}
                alt={movie.title}
                title={`${movie.title} - 海报与剧情解析`}
                className="aspect-[2/3] w-full object-cover object-top"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => toggleFavorite(movie.id)}
                className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  favorited
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "border border-background-300 bg-background-50 text-foreground-800 hover:border-accent-400 hover:text-accent-600"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex flex-1 items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="mb-4 text-base font-bold text-foreground-950">基本信息</h2>
              <ul className="flex flex-col gap-3">
                {metaList.map((meta) => (
                  <li key={meta.label} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background-100 text-primary-600">
                      <i className={meta.icon} />
                    </span>
                    <span className="text-foreground-500 whitespace-nowrap">{meta.label}</span>
                    <span className="ml-auto text-right text-foreground-900">{meta.value}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" />
                  {formatViews(movie.views)} 次浏览
                </span>
                <span>更新于 {movie.updatedAt}</span>
              </div>
            </div>
          </aside>

          <div className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <h2 className="mb-3 text-lg font-bold text-foreground-950 md:text-xl">作品简介</h2>
              <p className="text-sm leading-relaxed text-foreground-700 md:text-base">
                {movie.summary}
              </p>
            </div>
            <PlotSection plot={movie.plot} />
            <CharacterSection characters={movie.characters} />
          </div>
        </div>

        <div className="mt-10 md:mt-14">
          <h2 className="mb-5 flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
            <span className="h-5 w-1 rounded-full bg-accent-500" />
            相关推荐
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </div>
      </section>

      {tip && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 rounded-md border border-primary-800 bg-primary-950 px-5 py-3 text-sm text-background-50">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-accent-400" />
            {tip}
          </span>
        </div>
      )}
    </>
  );
}